import { supportedFileTypes as allSupportedFileTypes } from '@unifyapps/defs/blocks/FileUpload/contentSchema';
import type { FileType } from '@unifyapps/defs/blocks/FileUpload/types';
import { useTranslation } from '@unifyapps/i18n/client';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import type { UploadedFile } from '../../hooks/useUppy';
import { getBucketAndKey, type UploadResponse } from '../../hooks/useUppy/getPrivateUrl';
import type { FileUploaderFieldType } from './FileUploaderField';

export const getLogoInfo = (url?: string): FileUploaderFieldType | undefined => {
  if (!url) return undefined;
  const fileName = url.split('/').filter(Boolean).at(-1) ?? '';
  return {
    sourceType: 'URL',
    type: 'FILE',
    name: fileName,
    url,
    id: fileName,
  };
};

/**
 * temporary url ---\> to be deprecated
 * url ---\> while uploading, blob url and when uploaded, url path from backend
 */
export const getFileTypeFromUploadedFile = (file: UploadedFile): Omit<FileType, 'extension'> => {
  // use URL source type if key is not present(for non s3 multipart types), else use CLOUD_STORAGE
  const useUrlSourceType = !file.uploadResponse?.key || file.meta?.publicAcl;
  let sourceType: FileType['sourceType'] = useUrlSourceType ? 'URL' : 'CLOUD_STORAGE';
  let source = useUrlSourceType ? file.url : file.uploadResponse?.key;

  // if for url source type, key is not present and publicAcl is false, then we try to get the key from the location url
  if (useUrlSourceType && !file.meta?.publicAcl) {
    const resp = file.uploadResponse as UploadResponse['body'];
    const { Key } = getBucketAndKey(resp?.location, resp);
    if (Key) {
      sourceType = 'CLOUD_STORAGE';
      source = Key;
    }
  }

  return {
    id: file.id,
    name: file.name,
    url: file.url ?? '', // this is the download url
    sourceType,
    source,
    fileType: file.extension,
    size: file.size,
    type: 'FILE',
    mimeType: file.type,
    progress: 100,
    isUploaded: true,
    error: file.error,
    // added this meta because when the file upload is being done,
    // i had passed some meta to this file which i need to decide in the no-code
    meta: file.meta,
    hasError: Boolean(file.error),
  };
};

export const getFileTypeFromFileInProgress = (file: UploadedFile): FileType => {
  // upload-success dispatches AddOrUpdateFile which overwrites state — our custom
  // fileProgress is lost and uppy's file.progress.percentage is not guaranteed to be
  // 100 at that instant. treat uploadResponse as the authoritative completion signal
  // so re-renders triggered by adding another file don't flip completed files back to loading.
  const isUploaded = Boolean(file.uploadResponse);
  const progress = isUploaded
    ? 100
    : Math.min(file.fileProgress || file.progress?.percentage || 0, 99);
  return {
    id: file.id,
    hasError: Boolean(file.error),
    name: file.name,
    url: file.meta?.previewUrl ?? '', // this is the blob url
    sourceType: 'CLOUD_STORAGE',
    fileType: file.extension,
    size: file.size,
    type: 'FILE',
    mimeType: file.type,
    meta: file.meta,
    error: file.error,
    progress,
    isUploaded, //uploadResponse is present only when file is uploaded
  };
};

const supportedFileTypesMap = keyBy(allSupportedFileTypes, 'const');

// Expand JPG support to include JPEG, as .jpg files are identified as JPEG format by some systems
function expandJpgAliases(fileTypes: string[]): string[] {
  if (fileTypes.includes('image/jpg') && !fileTypes.includes('image/jpeg')) {
    return [...fileTypes, 'image/jpeg'];
  }

  return fileTypes;
}

export function useGetAcceptedFileTypes({
  supportedFileTypes,
  maxFiles,
  uploaderDetails,
}: {
  supportedFileTypes: string[] | undefined;
  maxFiles?: number;
  uploaderDetails?: { label?: string };
}) {
  const { t } = useTranslation(['common']);

  const isAcceptingAllFileTypes =
    !supportedFileTypes || isEmpty(supportedFileTypes) || supportedFileTypes.includes('*');

  if (isAcceptingAllFileTypes) {
    return {
      acceptFileTypes: '*',
      uploaderDescription: {
        label:
          (maxFiles || 0) > 1
            ? t('common:Uppy.MaxFilesCountDescription', {
                fileTypes: t('common:Uppy.AllFileTypes'),
                count: maxFiles,
              })
            : t('common:Uppy.SupportedFileTypes', {
                fileTypes: t('common:Uppy.AllFileTypes'),
              }),
      },
    };
  }

  const expandedFileTypes = expandJpgAliases(supportedFileTypes);
  const acceptFileTypes = expandedFileTypes.map((value) => value).join(', ');
  const acceptingFileTypesLabel = supportedFileTypes
    .map((value) => supportedFileTypesMap[value].title)
    .join(', ');

  return {
    acceptFileTypes,
    uploaderDescription: {
      label:
        uploaderDetails?.label ??
        ((maxFiles || 0) > 1
          ? t('common:Uppy.MaxFilesCountDescription', {
              fileTypes: acceptingFileTypesLabel,
              count: maxFiles,
            })
          : t('common:Uppy.SupportedFileTypes', {
              fileTypes: acceptingFileTypesLabel,
            })),
    },
  };
}
